import type { Quantity } from '~/graphql/graphql.ts';
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Minus, Package, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PiecesPerBundleEditor } from '~/components/ui/PiecesPerBundleEditor';
import { PiecesPerBundleLabel } from '~/components/ui/PiecesPerBundleLabel';
import { VehicleCapacityEditor } from '~/components/ui/VehicleCapacityEditor';
import { WarningBanner } from '~/components/ui/Banner';
import type { TransportMode } from '~/hooks/useTransportState';
import { useDebounce } from '~/hooks/useDebounce';
import {
  bundleCountForQuantity,
  calcUnitsPerBundleForProductLength,
  hasBundleDimensions,
  quantityKeepingBundleCount,
  type BundleProductLengthLike,
} from '~/components/ui/bundleQuantity';

export type { BundleProductLengthLike };

type ItemQuantityProps = {
  onBundleChange: (itemQuantity: Quantity) => void;
  disabled?: boolean;
  platformProductsLength: BundleProductLengthLike;
  quantity: Quantity;
  vehiclePayload?: Quantity | null;
  vehicleLabel?: TransportMode;
  onCapacitySubmit?: (value: number) => void;
  piecesPerBundle?: number;
  orderLinePiecesPerBundle?: number;
  /** Receives the new bundle size and the quantity that keeps the bundle count. */
  onPiecesPerBundleChange?: (value: number, quantity: Quantity) => void;
  onPiecesPerBundleClear?: (quantity: Quantity) => void;
};

export function BundleQuantityInput({
  onBundleChange,
  disabled = false,
  platformProductsLength,
  quantity,
  vehiclePayload,
  vehicleLabel,
  onCapacitySubmit,
  piecesPerBundle,
  orderLinePiecesPerBundle,
  onPiecesPerBundleChange,
  onPiecesPerBundleClear,
}: Readonly<ItemQuantityProps>) {
  const fallbackPiecesPerBundle =
    orderLinePiecesPerBundle ??
    platformProductsLength.platformProduct.piecesPerBundle.value;
  const effectivePiecesPerBundle = piecesPerBundle ?? fallbackPiecesPerBundle;

  const quantityKeepingBundleCountFor = (newPiecesPerBundle: number) =>
    quantityKeepingBundleCount(
      quantity,
      effectivePiecesPerBundle,
      newPiecesPerBundle,
      platformProductsLength
    );

  const unitsPerBundle = useMemo(() => {
    return calcUnitsPerBundleForProductLength(
      quantity,
      effectivePiecesPerBundle,
      platformProductsLength
    );
  }, [quantity, effectivePiecesPerBundle, platformProductsLength]);

  const bundleEntryDisabled = disabled || unitsPerBundle <= 0;

  const initBundleCount = bundleCountForQuantity(quantity, unitsPerBundle);
  const [bundleCount, setBundleCount] = useState<number>(() => initBundleCount);
  const linePieces =
    quantity.unit === 'PCS' ? 0 : bundleCount * effectivePiecesPerBundle;
  const [bundleCountText, setBundleCountText] = useState<string>(() =>
    formatQuantityValue(initBundleCount)
  );
  const [unitValue, setUnitValue] = useState<string>(() =>
    formatQuantityValue(quantity.value, quantity.unit)
  );
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [adjustmentMessage, setAdjustmentMessage] = useState<string | null>(
    null
  );
  const [prevQuantityValue, setPrevQuantityValue] = useState<number>(
    quantity.value
  );
  const [prevQuantityUnit, setPrevQuantityUnit] = useState<string>(
    quantity.unit
  );
  const [prevUnitsPerBundle, setPrevUnitsPerBundle] =
    useState<number>(unitsPerBundle);
  const adjustmentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Sync with external prop changes — guard on value, unit, AND unitsPerBundle
  // so that switching products (different bundle size) or preferred units
  // (different unit label) both trigger a re-sync even if value stays the same.
  if (
    quantity.value !== prevQuantityValue ||
    quantity.unit !== prevQuantityUnit ||
    unitsPerBundle !== prevUnitsPerBundle
  ) {
    const newBundleCount = bundleCountForQuantity(quantity, unitsPerBundle);
    setBundleCount(newBundleCount);
    setBundleCountText(formatQuantityValue(newBundleCount));
    setUnitValue(formatQuantityValue(quantity.value, quantity.unit));
    setPrevQuantityValue(quantity.value);
    setPrevQuantityUnit(quantity.unit);
    setPrevUnitsPerBundle(unitsPerBundle);
  }

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (adjustmentTimeoutRef.current) {
        clearTimeout(adjustmentTimeoutRef.current);
      }
    };
  }, []);

  const { t } = useTranslation('cart');

  const performCalculation = useCallback(
    (inputValue: string) => {
      const parsedValue = parseFloat(inputValue);

      if (isNaN(parsedValue) || parsedValue < 0) {
        // Treat invalid/negative as 0
        setBundleCount(0);
        setBundleCountText('0');
        setUnitValue('0');
        setIsCalculating(false);
        onBundleChange({ value: 0, unit: quantity.unit });
        return;
      }

      if (unitsPerBundle <= 0) {
        setIsCalculating(false);
        return;
      }

      const newBundleCount = Math.max(
        1,
        Math.ceil(parsedValue / unitsPerBundle)
      );
      const newActualUnitValue = newBundleCount * unitsPerBundle;
      const roundedUnitValue =
        Math.round((newActualUnitValue + Number.EPSILON) * 1000) / 1000;

      // Check if rounding occurred
      if (Math.abs(parsedValue - roundedUnitValue) > 0.001) {
        setAdjustmentMessage(
          t('cart.adjusted_to_bundle_quantity', {
            fromValue: formatQuantityValue(parsedValue, quantity.unit),
            fromUom: quantity.unit,
            toValue: formatQuantityValue(roundedUnitValue, quantity.unit),
            toUom: quantity.unit,
          })
        );

        // Clear existing timeout and auto-dismiss after 3 seconds
        if (adjustmentTimeoutRef.current) {
          clearTimeout(adjustmentTimeoutRef.current);
        }
        adjustmentTimeoutRef.current = setTimeout(() => {
          setAdjustmentMessage(null);
        }, 3000);
      } else {
        // Clear adjustment message if no rounding needed
        if (adjustmentTimeoutRef.current) {
          clearTimeout(adjustmentTimeoutRef.current);
        }
        setAdjustmentMessage(null);
      }

      setBundleCount(newBundleCount);
      setBundleCountText(formatQuantityValue(newBundleCount));
      setUnitValue(formatQuantityValue(roundedUnitValue, quantity.unit));
      onBundleChange({ value: roundedUnitValue, unit: quantity.unit });
      setIsCalculating(false);
    },
    [unitsPerBundle, quantity.unit, onBundleChange, t]
  );

  const debouncedCalculation = useDebounce(performCalculation, 500);

  const handleIncrement = useCallback(() => {
    const newBundleCount = bundleCount + 1;
    const newValue =
      Math.round((newBundleCount * unitsPerBundle + Number.EPSILON) * 1000) /
      1000;
    setBundleCount(newBundleCount);
    setBundleCountText(formatQuantityValue(newBundleCount));
    setUnitValue(formatQuantityValue(newValue, quantity.unit));
    onBundleChange({ value: newValue, unit: quantity.unit });
  }, [bundleCount, unitsPerBundle, quantity.unit, onBundleChange]);

  const handleDecrement = useCallback(() => {
    const newBundleCount = Math.max(0, bundleCount - 1);
    const newValue =
      Math.round((newBundleCount * unitsPerBundle + Number.EPSILON) * 1000) /
      1000;
    setBundleCount(newBundleCount);
    setBundleCountText(formatQuantityValue(newBundleCount));
    setUnitValue(formatQuantityValue(newValue, quantity.unit));
    onBundleChange({ value: newValue, unit: quantity.unit });
  }, [bundleCount, unitsPerBundle, quantity.unit, onBundleChange]);

  if (!hasBundleDimensions(platformProductsLength, quantity.unit)) {
    return (
      <WarningBanner testId="bundle-quantity-missing-dimensions">
        {t('cart.missing_dimensions', { unit: quantity.unit })}
      </WarningBanner>
    );
  }

  return (
    <div className="flex w-full flex-col gap-1">
      <div>
        <div className="flex items-center gap-1">
          <input
            type="text"
            name="unit-quantity"
            aria-label="unit quantity"
            inputMode="numeric"
            className="h-6 flex-1 text-center text-xs border border-neut-300 rounded focus:outline-none focus:ring-1 focus:ring-prim focus:border-prim"
            value={unitValue}
            onChange={(e) => {
              const newValue = e.target.value;
              setUnitValue(newValue);
              setIsCalculating(true);
              debouncedCalculation(newValue);
            }}
            onBlur={() => {
              // Cancel pending debounce and execute immediately
              debouncedCalculation.cancel();
              performCalculation(unitValue);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur();
              }
            }}
            disabled={bundleEntryDisabled}
          />
          <span
            className="text-[11px] text-neut-sec"
            data-testid="quantity-input-uom"
          >
            {quantity.unit}
          </span>
        </div>
      </div>

      <div className="text-[11px] text-neut-600 mb-1 font-medium uppercase flex items-center gap-2">
        {isCalculating && (
          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[9px] font-semibold">
            {t('cart.update_bundle_quantity')}
          </span>
        )}
      </div>
      <div className="flex w-full items-center gap-1">
        <button
          type="button"
          className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 cursor-pointer"
          name="decrement"
          onClick={handleDecrement}
          disabled={bundleCount <= 0 || bundleEntryDisabled}
        >
          <Minus className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
        <input
          type="text"
          name="quantity"
          aria-label="quantity"
          aria-busy={isCalculating}
          inputMode="numeric"
          className={`h-7 min-w-16 flex-1 text-center text-xs border rounded focus:outline-none focus:ring-1 focus:ring-prim focus:border-prim ${
            isCalculating ? 'bg-blue-50 border-blue-500' : 'border-neut-300'
          }`}
          value={bundleCountText}
          onChange={(e) => {
            if (!/^\d*$/.test(e.target.value)) return;
            const raw = e.target.value;
            setBundleCountText(raw);
            if (raw === '') {
              setBundleCount(0);
              return;
            }
            const value = parseInt(raw, 10);
            if (!isNaN(value) && value >= 0) {
              const newValue =
                Math.round((value * unitsPerBundle + Number.EPSILON) * 1000) /
                1000;
              setBundleCount(value);
              setUnitValue(formatQuantityValue(newValue, quantity.unit));
            }
          }}
          onBlur={() => {
            if (unitsPerBundle <= 0) return;
            const parsed = parseInt(bundleCountText, 10);
            const resolvedBundleCount =
              !isNaN(parsed) && parsed >= 0 ? parsed : 0;
            const roundedValue =
              Math.round(
                (resolvedBundleCount * unitsPerBundle + Number.EPSILON) * 1000
              ) / 1000;
            setBundleCount(resolvedBundleCount);
            setBundleCountText(formatQuantityValue(resolvedBundleCount));
            setUnitValue(formatQuantityValue(roundedValue, quantity.unit));
            onBundleChange({ value: roundedValue, unit: quantity.unit });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          disabled={bundleEntryDisabled}
        />
        <button
          type="button"
          name="increment"
          className="w-7 h-7 rounded border border-neut-300 flex items-center justify-center hover:bg-neut-50 transition-colors text-neut-600 cursor-pointer"
          onClick={handleIncrement}
          disabled={bundleEntryDisabled}
        >
          <Plus className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
        <span className="pl-2 text-xs text-neut-sec whitespace-nowrap">
          {t('cart.bundles', { count: bundleCount })}
        </span>
      </div>

      {adjustmentMessage && (
        <div className="text-[11px] text-blue-600 mt-2 bg-blue-50 px-2 py-1.5 rounded border-l-4 border-blue-500">
          {adjustmentMessage}
        </div>
      )}

      {onPiecesPerBundleChange ? (
        <PiecesPerBundleEditor
          piecesPerBundle={effectivePiecesPerBundle}
          linePieces={linePieces}
          onChange={(value) =>
            onPiecesPerBundleChange(value, quantityKeepingBundleCountFor(value))
          }
          onClear={
            onPiecesPerBundleClear &&
            (() =>
              onPiecesPerBundleClear(
                quantityKeepingBundleCountFor(fallbackPiecesPerBundle)
              ))
          }
          disabled={disabled}
        />
      ) : (
        <PiecesPerBundleLabel
          piecesPerBundle={effectivePiecesPerBundle}
          linePieces={linePieces}
        />
      )}
      {unitsPerBundle > 0 && (
        <p className="text-xs text-neut-sec mt-1 flex items-center gap-1">
          <Package size={12} aria-hidden="true" />
          <QuantityComponent
            quantity={{ value: unitsPerBundle, unit: quantity.unit }}
          />
          /{t('cart.bundles_one')}
          {quantity.unit !== 'PCS' && (
            <span className="text-neut-400">
              ({effectivePiecesPerBundle} PCS)
            </span>
          )}
        </p>
      )}
      <VehicleCapacityEditor
        platformProductsLength={platformProductsLength}
        quantityUnit={quantity.unit}
        vehiclePayload={vehiclePayload}
        vehicleLabel={vehicleLabel}
        onCapacitySubmit={onCapacitySubmit}
        disabled={disabled}
      />
    </div>
  );
}

type QuantityComponentProps = {
  quantity: Quantity;
  'data-testid'?: string;
};
const QuantityComponent = ({
  quantity,
  'data-testid': dataTestId,
}: QuantityComponentProps) => {
  return (
    <span>
      <span data-testid={dataTestId ? `${dataTestId}-value` : undefined}>
        {formatQuantityValue(quantity.value, quantity.unit)}
      </span>{' '}
      <span data-testid={dataTestId ? `${dataTestId}-unit` : undefined}>
        {quantity.unit}
      </span>
    </span>
  );
};

function formatQuantityValue(value: number, unit?: string): string {
  if (unit === 'PCS') return Math.round(value).toString();
  const fixedValue = Number(value.toFixed(3));
  if (value > 0 && fixedValue === 0) return '< 0.001';
  return Number.isInteger(fixedValue)
    ? fixedValue.toString()
    : fixedValue.toFixed(3);
}
